import * as React from 'react'

import { Reorder } from 'framer-motion'

import NoItemsMessage from './_NoItemsMessage'
import styles from './index.module.css'

interface Props<T> {
  items: T[]
  itemAsKey: (item: T, index: number) => string
  renderItem: (item: T, index: number) => JSX.Element
  onSortEnd: (items: T[]) => void
  isDisabled?: (item: T) => boolean
  emptyMessage: string
  trackingProperties?: Record<string, unknown> | undefined
}

function SortableBoxList<T>({
  items: initialItems,
  itemAsKey,
  renderItem,
  onSortEnd,
  emptyMessage,
  isDisabled,
  children,
}: React.PropsWithChildren<Props<T>>) {
  const [items, setItems] = React.useState<T[]>([])

  React.useEffect(() => {
    setItems(initialItems)
  }, [initialItems])

  const onReorder = (items: T[]) => {
    setItems(items)
    onSortEnd(items)
  }

  if (children) return <>{children}</>

  if (!items.length) {
    return <NoItemsMessage>{emptyMessage}</NoItemsMessage>
  }

  return (
    <Reorder.Group axis="y" values={items} onReorder={onReorder} className={styles.list}>
      {items.map((item, index) => (
        <Reorder.Item
          key={itemAsKey(item, index)}
          value={item}
          className={styles.item}
          dragListener={isDisabled ? !isDisabled(item) : true}
        >
          {renderItem(item, index)}
        </Reorder.Item>
      ))}
    </Reorder.Group>
  )
}

export default SortableBoxList
